import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { V2apiClient, isErrorResponse } from '../../ApiClient';
import { PageBackground } from '../../ui/PageBackground';
import { Panel } from '../../ui/Panel';
import { Text } from '../../ui/Text';
import { Button } from '../../ui/Button';

/*
 * JoinGame — target of an invite link (/join/:code). Resolves the caller's
 * session and seats them at the invited table:
 *  - Not signed in → send to Home to register, preserving the invite as a
 *    ?redirect so we return here once they have a session.
 *  - Signed in → join by code and go to the game, or show why we couldn't.
 */

interface Props {
  setPlayerCallback: (player: { id: string; name: string }) => void;
}

export const JoinGame: React.FC<Props> = ({ setPlayerCallback }) => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!code) {
        setError('This invite link is invalid.');
        return;
      }

      const me = await V2apiClient.getMe();
      if (cancelled) return;

      if (!me) {
        // No session yet — register first, then come back to this invite.
        navigate(`/?redirect=${encodeURIComponent(`/join/${code}`)}`, {
          replace: true,
        });
        return;
      }
      setPlayerCallback(me.user);

      const res = await V2apiClient.joinGameByCode(code);
      if (cancelled) return;

      if (res.status === 200) {
        navigate('/setback', { replace: true });
      } else {
        const body = res.body;
        setError((isErrorResponse(body) && body.error) || "This game can't be joined right now.");
      }
    })().catch(() => {
      if (!cancelled) setError('Connection error. Try again.');
    });

    return () => {
      cancelled = true;
    };
  }, [code, navigate, setPlayerCallback]);

  return (
    <PageBackground>
      <Panel
        style={{
          position: 'relative',
          zIndex: 1,
          width: '360px',
          padding: '44px 52px',
          textAlign: 'center',
        }}
      >
        {error ? (
          <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
            <Text as="h1" variant="heading">
              Can't join game
            </Text>
            <Text variant="muted">{error}</Text>
            <Button variant="primary" onClick={() => navigate('/lobby')}>
              Go to Lobby
            </Button>
          </div>
        ) : (
          <Text variant="value">Joining game…</Text>
        )}
      </Panel>
    </PageBackground>
  );
};
